import { useEffect, useState } from 'react'

const SESSION_KEY = 'gritvue-objection-shown'
const TIME_ON_PAGE_MS = 45_000
const SCROLL_THRESHOLD = 0.4

/**
 * Opens the objection modal only for visitors who engaged and did not buy:
 * 60s on the page, 40%+ scrolled, the offer section actually seen, no buy
 * intent, and never more than once per session.
 */
export function useObjectionTrigger() {
  const [open, setOpen] = useState(false)
  const [timeReady, setTimeReady] = useState(false)
  const [scrollReady, setScrollReady] = useState(false)
  const [offerSeen, setOfferSeen] = useState(false)
  const [hasIntent, setHasIntent] = useState(false)
  const [done, setDone] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.sessionStorage.getItem(SESSION_KEY) === '1'
  })

  useEffect(() => {
    const timer = window.setTimeout(() => setTimeReady(true), TIME_ON_PAGE_MS)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if (window.scrollY / scrollable >= SCROLL_THRESHOLD) setScrollReady(true)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const target = document.getElementById('offer')
    if (!target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setOfferSeen(true)
      },
      { threshold: 0.15 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  // Any move toward buying disables the modal for the rest of the session.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const el = event.target as HTMLElement | null
      if (!el) return
      if (el.closest('[data-buy-intent], a[href*="#offer"], [id^="pack-"][id$="-cta"]')) {
        setHasIntent(true)
      }
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  useEffect(() => {
    if (done || hasIntent) return
    if (timeReady && scrollReady && offerSeen) {
      setOpen(true)
      setDone(true)
      window.sessionStorage.setItem(SESSION_KEY, '1')
    }
  }, [done, hasIntent, timeReady, scrollReady, offerSeen])

  // Reopened only on purpose, from "choose another" inside the recovery area.
  return { open, close: () => setOpen(false), reopen: () => setOpen(true) }
}
